import { Settings, Eye } from 'lucide-react';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import dayjs from 'utils/extendedDayJS';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import {
  DataViewLine,
  DataViewLineIcon,
  DataViewLineContent,
  DataViewLineHeading,
  DataViewLineSubText
} from '@epoch-ui/components/DataViewLine/DataViewLine';
import { EventCalendarIcon } from 'components/common/icons/EventCalendarIcon';
import PersonInviteIcon from 'components/common/icons/PersonInviteIcon';
import { CalendarTransparency } from 'types/event.types';
import { DETAILED_DATETIME_FORMAT as DATE_FORMAT } from 'constants/dates';
import { useToaster } from 'hooks/useToaster';
import { ToasterVariant } from 'context/toasterContext';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import { CalendarSettingsModal } from '../../../../components/CalendarSettingsModal';
import { updateCalendarSettings } from '../../../api/feedback/updateCalendarSettings';
import { CalendarEventFormValues } from '../../../../components/calendarSettings.schema';

interface CalendarDataLineProps {
  icon: React.ReactNode;
  heading: string;
  subText?: string;
}

const CalendarDataLine: React.FC<CalendarDataLineProps> = ({ icon, heading, subText }) => (
  <DataViewLine>
    <DataViewLineIcon>{icon}</DataViewLineIcon>
    <DataViewLineContent>
      <DataViewLineHeading>{heading}</DataViewLineHeading>
      {subText && <DataViewLineSubText>{subText}</DataViewLineSubText>}
    </DataViewLineContent>
  </DataViewLine>
);

export const CalendarSettingsSection = (): React.ReactElement => {
  const {
    calendarDetails,
    sendCalendarInvites,
    transparency,
    calendarNotifications,
    isPublished,
    createdBy,
    id: eventId,
    scheduledDeletionAt,
    hasEnded
  } = useEditableEvent();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToaster();

  const isCancelled = !!scheduledDeletionAt;
  const isDisabled = isCancelled || hasEnded;

  const updateCalendarSettingsMutation = useMutation({
    mutationFn: updateCalendarSettings,
    onSuccess: () => {
      setIsModalOpen(false);
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(eventId!) });
    },
    onError: (error) => {
      console.error('Failed to update calendar settings:', error);
      showToast('Something went wrong. Please try again.', { variant: ToasterVariant.ERROR });
    }
  });

  /**
   * Events predating explicit calendar settings carry NULLs, which mean "never answered"
   * — not a value. Sync reads as off so saving can't create a calendar event the admin
   * never asked for, and an unanswered add-guests choice stays unset rather than being
   * invented as 'rsvped', which had the modal looking settled on events the API rejects.
   */
  const getAddGuestsSetting = (): CalendarEventFormValues['calendarAddGuestsSetting'] => {
    if (sendCalendarInvites == null) return undefined;
    return sendCalendarInvites ? 'invited' : 'rsvped';
  };

  const getInitialFormValues = (): Partial<CalendarEventFormValues> => ({
    calendarNotifications: calendarNotifications ?? false,
    calendarAddGuestsSetting: getAddGuestsSetting(),
    calendarEventVisibility: transparency === CalendarTransparency.TRANSPARENT ? 'free' : 'busy'
  });

  const handleModalSubmit = async (values: CalendarEventFormValues): Promise<void> => {
    updateCalendarSettingsMutation.mutate({
      eventId: eventId!,
      calendarNotifications: values.calendarNotifications,
      sendCalendarInvites: values.calendarNotifications
        ? values.calendarAddGuestsSetting === 'invited'
        : false,
      transparency:
        values.calendarEventVisibility === 'busy'
          ? CalendarTransparency.OPAQUE
          : CalendarTransparency.TRANSPARENT
    });
  };

  const calendarDataLines: CalendarDataLineProps[] = calendarNotifications
    ? [
        {
          icon: <EventCalendarIcon />,
          heading: `${createdBy?.firstName ?? ''} ${createdBy?.lastName ?? ''}'s calendar`,
          subText: calendarDetails?.lastUpdatedAt
            ? `Last synced: ${dayjs(calendarDetails.lastUpdatedAt).format(DATE_FORMAT)}`
            : undefined
        },
        {
          icon: <PersonInviteIcon className="tw-h-5 tw-w-5 tw-text-black" />,
          heading: 'Guests',
          subText: sendCalendarInvites
            ? 'Added upon invitation to the event'
            : 'Added after they RSVP to the event'
        },
        {
          icon: <Eye className="tw-h-5 tw-w-5 tw-text-black" />,
          heading: 'Event visibility',
          subText: transparency === CalendarTransparency.OPAQUE ? 'Busy' : 'Free'
        }
      ]
    : [
        {
          icon: <EventCalendarIcon />,
          heading: 'No calendar event created'
        }
      ];

  return (
    <div className="tw-space-y-4">
      <div className="tw-flex tw-items-center tw-justify-between">
        <h2
          className={cn(
            'tw-m-0 tw-text-xl tw-font-medium tw-leading-7',
            isDisabled && 'tw-opacity-40'
          )}
        >
          Calendar settings
        </h2>
        <Button
          size="xxs"
          className="tw-gap-1 tw-rounded-xl tw-leading-4"
          variant="secondary"
          onClick={() => setIsModalOpen(true)}
          disabled={isDisabled}
        >
          <Settings className="tw-text-black" size={16} />
          Settings
        </Button>
      </div>

      <div className={cn('tw-space-y-3', isDisabled && 'tw-opacity-40')}>
        {calendarDataLines.map((line, index) => (
          <CalendarDataLine
            key={index}
            icon={line.icon}
            heading={line.heading}
            subText={line.subText}
          />
        ))}
      </div>

      <CalendarSettingsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        initialValues={getInitialFormValues()}
        creatorDetails={createdBy}
        onSubmit={handleModalSubmit}
        disabled={!!isPublished}
      />
    </div>
  );
};
